import { generatedAvatarDataUrl } from '@metorial/brand';
import { Paths } from '@metorial/frontend-config';
import { createProject } from '@metorial/layout';
import { Avatar, theme } from '@metorial/ui';
import * as Popover from '@radix-ui/react-popover';
import { RiAddLine, RiArrowRightSLine, RiExpandUpDownLine } from '@remixicon/react';
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import type { ShellScope } from './scope';

// The top-left breadcrumb pill: [org] Blackboard Studio › BlackOS. In the
// Workforce section the second crumb is the portal, elsewhere the project.
// Opens "Switch Workspace" with the projects and portals (and organizations).

let Trigger = styled.button`
  height: 38px;
  max-width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px 0 6px;
  border: none;
  border-radius: 11px;
  background: ${theme.colors.gray400};
  color: ${theme.colors.gray900};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover,
  &:focus-visible,
  &[data-state='open'] {
    background: ${theme.colors.gray500};
  }

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  svg {
    flex-shrink: 0;
    color: ${theme.colors.gray650};
  }
`;

let Content = styled(Popover.Content)`
  z-index: 999;
  width: 320px;
  max-height: 70vh;
  overflow-y: auto;
  background: rgba(250, 250, 250, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid ${theme.colors.gray400};
  border-radius: 14px;
  box-shadow: ${theme.shadows.large};
  padding: 6px;
`;

let Header = styled.div`
  padding: 10px 10px 8px;
  border-bottom: 1px solid ${theme.colors.gray300};
  margin-bottom: 6px;

  b {
    display: block;
    font-size: 14px;
  }

  small {
    font-size: 12px;
    color: ${theme.colors.gray650};
  }
`;

let SectionLabel = styled.p`
  padding: 8px 10px 4px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${theme.colors.gray600};
`;

let Row = styled.span`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 10px;
  border-radius: 8px;
  font-size: 14px;
  color: ${theme.colors.gray800};
  cursor: pointer;

  &:hover {
    background: ${theme.colors.gray300};
  }

  span {
    flex-grow: 1;
    min-width: 0;
    overflow-wrap: anywhere;
  }
`;

let ActiveDot = styled.i`
  width: 7px;
  height: 7px;
  border-radius: 99px;
  background: ${theme.colors.primary};
  flex-shrink: 0;
`;

let RowButton = styled.button`
  display: block;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  text-align: left;
`;

let withAvatar = (e: { id: string; name: string; imageUrl?: string | null }) => ({
  name: e.name,
  imageUrl: e.imageUrl || generatedAvatarDataUrl(e.id)
});

export let WorkspaceSwitcher = ({
  scope,
  showPortal
}: {
  scope: ShellScope;
  showPortal: boolean;
}) => {
  let [open, setOpen] = useState(false);
  let { pathname } = useLocation();
  useEffect(() => setOpen(false), [pathname]);

  let organization = scope.organization;
  if (!organization) return null;

  let boot = scope.boot.data;
  let organizations = boot?.organizations ?? [];
  let projects = (boot?.projects ?? []).filter(p => p.organizationId == organization.id);
  let portal = scope.currentPortal ?? scope.defaultPortal;
  let second = showPortal ? portal?.name : scope.project?.name;

  let projectHref = (project: (typeof projects)[number]) => {
    let instances = (boot?.instances ?? []).filter(i => i.project.id == project.id);
    let instance = instances.find(i => i.type == 'production') ?? instances[0];
    return instance ? Paths.instance.home(organization, project, instance) : '#';
  };

  let orgHref = (org: (typeof organizations)[number]) => {
    let instance =
      (boot?.instances ?? []).find(
        i => i.organizationId == org.id && i.type == 'production'
      ) ?? (boot?.instances ?? []).find(i => i.organizationId == org.id);
    let project = (boot?.projects ?? []).find(p => p.id == instance?.project.id);
    return instance && project
      ? Paths.instance.home(org, project, instance)
      : Paths.organization.settings(org);
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Trigger type="button" aria-label="Switch organization or project">
          <Avatar entity={organization} size={26} noTooltip />
          <span>{organization.name}</span>
          {second && (
            <>
              <RiArrowRightSLine size={15} />
              <span>{second}</span>
            </>
          )}
          <RiExpandUpDownLine size={14} />
        </Trigger>
      </Popover.Trigger>

      <Popover.Portal>
        <Content side="bottom" align="start" sideOffset={6}>
          <Header>
            <b>Switch Workspace</b>
            <small>Browse your organization's projects and portals.</small>
          </Header>

          {organizations.length > 1 && (
            <>
              <SectionLabel>Organizations</SectionLabel>
              {organizations.map(org => (
                <Link key={org.id} to={orgHref(org)}>
                  <Row>
                    <Avatar entity={org} size={22} noTooltip />
                    <span>{org.name}</span>
                    {org.id == organization.id && <ActiveDot />}
                  </Row>
                </Link>
              ))}
            </>
          )}

          <SectionLabel>Projects</SectionLabel>
          {projects.map(project => (
            <Link key={project.id} to={projectHref(project)}>
              <Row>
                <Avatar entity={withAvatar(project)} size={22} noTooltip />
                <span>{project.name}</span>
                {project.id == scope.project?.id && <ActiveDot />}
              </Row>
            </Link>
          ))}
          <RowButton
            type="button"
            onClick={() => {
              setOpen(false);
              createProject(organization as any);
            }}
          >
            <Row>
              <RiAddLine size={18} />
              <span>Create Project</span>
            </Row>
          </RowButton>

          {scope.portals.length > 0 && scope.instance && (
            <>
              <SectionLabel>Portals</SectionLabel>
              {scope.portals.map(p => (
                <Link
                  key={p.id}
                  to={Paths.instance.workforcePortal.overview(
                    organization,
                    scope.project,
                    scope.instance,
                    p.id
                  )}
                >
                  <Row>
                    <Avatar entity={withAvatar(p)} size={22} noTooltip />
                    <span>{p.name}</span>
                    {showPortal && p.id == portal?.id && <ActiveDot />}
                  </Row>
                </Link>
              ))}
            </>
          )}
        </Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
